import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon, TrashIcon, DocumentDuplicateIcon, ClipboardDocumentIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../api/api';

const MarketingBudgetModal = ({ isOpen, onClose, onSave, editingData = null }) => {
  const [selectedMainGroup, setSelectedMainGroup] = useState('');
  const [selectedBudgetType, setSelectedBudgetType] = useState('');
  const [budgetEntries, setBudgetEntries] = useState([]);
  const [budgetYear, setBudgetYear] = useState('');
  const [budgetMonth, setBudgetMonth] = useState('');
  const [loading, setLoading] = useState(false);

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Şehir arama ve ekleme için state'ler
  const [citySearchTerm, setCitySearchTerm] = useState({});
  const [showCityDropdown, setShowCityDropdown] = useState({});
  const [isAddingNewCity, setIsAddingNewCity] = useState(false);
  const [newCityName, setNewCityName] = useState('');
  const cityDropdownRef = useRef({});

  // Sıralama state'i
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Ana grup'a göre alan konfigürasyonu
  const getFieldConfig = (maingroupName) => {
    switch (maingroupName) {
      case 'İlan Reklam':
        return {
          showFields: ['country', 'city', 'agency', 'subgroup', 'market', 'budget_amount', 'budget_month', 'summary'],
          hideFields: ['departure', 'arrival', 'person_name', 'start_date', 'end_date', 'fair_name', 'quantity']
        };
      case 'Fuar Detay':
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'agency', 'start_date', 'end_date', 'pax', 'budget_amount'],
          hideFields: ['departure', 'arrival', 'person_name', 'summary', 'fair_name', 'quantity']
        };
      case 'Promosyon':
        return {
          showFields: ['market', 'country', 'city', 'agency', 'subgroup', 'quantity', 'budget_amount'],
          hideFields: ['departure', 'arrival', 'person_name', 'summary', 'start_date', 'end_date', 'fair_name']
        };
      case 'Workshop':
      case 'Work Shop Road Show':
        return {
          showFields: ['market', 'country', 'city', 'agency', 'subgroup', 'departure', 'arrival', 'summary', 'budget_amount'],
          hideFields: ['person_name', 'start_date', 'end_date', 'fair_name', 'quantity']
        };
      case 'Sales Call':
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'day_count', 'pax', 'pp', 'budget_amount'],
          hideFields: ['agency', 'person_name', 'summary', 'start_date', 'end_date', 'fair_name', 'quantity', 'departure', 'arrival', 'sales_call_type', 'meal_cost', 'cargo', 'hotel', 'transport', 'others', 'allowance', 'extra']
        };
      case 'Yılbaşı Hediye':
        return {
          showFields: ['subgroup', 'market', 'agency', 'budget_month', 'person_name', 'summary', 'budget_amount'],
          hideFields: ['country', 'city', 'departure', 'arrival', 'start_date', 'end_date', 'fair_name', 'quantity', 'day_count', 'pax', 'pp', 'sales_call_type', 'meal_cost', 'cargo', 'hotel', 'transport', 'others', 'allowance', 'extra']
        };
      case 'Web Yazılım':
        return {
          showFields: ['hotel', 'market', 'country', 'city', 'subgroup', 'summary', 'budget_amount', 'budget_month'],
          hideFields: ['agency', 'person_name', 'departure', 'arrival', 'start_date', 'end_date', 'fair_name', 'quantity', 'day_count', 'pax', 'pp', 'sales_call_type', 'meal_cost', 'cargo', 'transport', 'others', 'allowance', 'extra']
        };
      case 'Yurtdışı Temsilci':
        return {
          showFields: ['country', 'market', 'subgroup', 'budget_amount', 'summary'],
          hideFields: ['budget_month', 'city', 'agency', 'person_name', 'departure', 'arrival', 'start_date', 'end_date', 'fair_name', 'quantity', 'day_count', 'pax', 'pp', 'sales_call_type', 'meal_cost', 'cargo', 'hotel', 'transport', 'others', 'allowance', 'extra']
        };
      case 'Temsil Ağırlama':
        return {
          showFields: ['market', 'country', 'city', 'hotel', 'pax', 'budget_amount', 'budget_month', 'subgroup'],
          hideFields: ['agency', 'person_name', 'departure', 'arrival', 'start_date', 'end_date', 'fair_name', 'quantity', 'day_count', 'pp', 'summary', 'sales_call_type', 'meal_cost', 'cargo', 'transport', 'others', 'allowance', 'extra', 'region', 'eur_curr']
        };
      case 'AKKA Bonus':
        return {
          showFields: ['subgroup', 'budget_amount'],
          hideFields: ['market', 'country', 'city', 'agency', 'hotel', 'person_name', 'departure', 'arrival', 'start_date', 'end_date', 'fair_name', 'quantity', 'day_count', 'pax', 'pp', 'summary', 'sales_call_type', 'meal_cost', 'cargo', 'transport', 'others', 'allowance', 'extra', 'region', 'eur_curr', 'budget_month']
        };
      case 'Dijital Pazarlama':
        return {
          showFields: ['market', 'country', 'city', 'budget_month', 'region', 'subgroup', 'budget_amount', 'eur_curr'],
          hideFields: ['agency', 'person_name', 'departure', 'arrival', 'start_date', 'end_date', 'fair_name', 'quantity', 'day_count', 'pp', 'pax', 'summary', 'sales_call_type', 'meal_cost', 'cargo', 'hotel', 'transport', 'others', 'allowance', 'extra']
        };
      default:
        return {
          showFields: ['subgroup', 'market', 'country', 'city', 'agency', 'departure', 'arrival', 'person_name', 'summary', 'start_date', 'end_date', 'fair_name', 'quantity', 'budget_amount'],
          hideFields: []
        };
    }
  };

  const [referenceData, setReferenceData] = useState({
    maingroups: [],
    subgroups: [],
    markets: [],
    types: [],
    countries: [],
    cities: [],
    agencies: [],
    hotels: []
  });

  useEffect(() => {
    if (isOpen) {
      fetchReferenceData();

      if (!editingData || editingData.length === 0) {
        // Yeni kayıt modu - state'i temizle
        setSelectedMainGroup('');
        setBudgetEntries([]);
        setBudgetYear('');
        setBudgetMonth('');
      }
    }
  }, [isOpen]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Tüm açık dropdown'ları kontrol et
      Object.keys(showCityDropdown).forEach(entryId => {
        if (showCityDropdown[entryId]) {
          const dropdownElement = document.getElementById(`city-dropdown-${entryId}`);
          const inputElement = document.getElementById(`city-input-${entryId}`);

          if (dropdownElement && inputElement &&
            !dropdownElement.contains(event.target) &&
            !inputElement.contains(event.target)) {
            setShowCityDropdown(prev => ({ ...prev, [entryId]: false }));
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCityDropdown]);

  const loadEditingData = (data) => {
    if (!data || data.length === 0) return;

    // İlk kayıttan ana grup bilgisini al
    const firstEntry = data[0];

    setSelectedMainGroup(firstEntry.maingroup);
    setSelectedBudgetType(firstEntry.budgettype);
    setBudgetYear(firstEntry.budget_year || '');
    setBudgetMonth(firstEntry.budget_month || '');

    // Tüm kayıtları budgetEntries'e dönüştür
    const entries = data.map(entry => ({
      id: entry.id, // Mevcut kayıt ID'si (string veya number olabilir)
      maingroup: entry.maingroup || entry.maingroup_id, // Backend için
      maingroup_id: entry.maingroup || entry.maingroup_id, // Frontend için
      maingroup_name: entry.maingroup_name,
      subgroup: entry.subgroup || entry.subgroup_id, // Backend için
      subgroup_id: entry.subgroup || entry.subgroup_id, // Frontend için
      subgroup_name: entry.subgroup_name,
      market: entry.market || entry.market_id || null, // Backend için
      market_id: entry.market || entry.market_id || null, // Frontend için
      market_name: entry.market_name || '',
      budgettype: entry.budgettype || entry.budgettype_id, // Backend için
      budgettype_id: entry.budgettype || entry.budgettype_id, // Frontend için
      budgettype_name: entry.budgettype_name,
      budget_amount: entry.budget_amount || 0,
      budget_year: entry.budget_year || null,
      budget_month: entry.budget_month || null,
      budget_name: entry.budget_name || '',
      country: entry.country || '',
      city: entry.city || '',
      agency: entry.agency || '',
      departure: entry.departure || '',
      arrival: entry.arrival || '',
      person_name: entry.person_name || '',
      summary: entry.summary || '',
      start_date: entry.start_date || '',
      end_date: entry.end_date || '',
      // Promosyon ve Fuar için
      fair_name: entry.fair_name || '',
      quantity: entry.quantity || 0,
      // Sales Call için
      day_count: entry.day_count || 0,
      pax: entry.pax || 0,
      pp: entry.pp || 0,
      // Temsil Ağırlama için
      hotel_name: entry.hotel_name || '',
      // Dijital Pazarlama için
      region: entry.region || '',
      eur_curr: entry.eur_curr || null
    }));

    setBudgetEntries(entries);
  };

  const fetchReferenceData = async () => {
    try {
      setLoading(true);

      // Sadece ana grupları çekelim önce
      const maingroupsData = await apiRequest('/marketing-budget/maingroups/');

      // Şimdi diğer verileri çekelim
      const [subgroups, markets, types, countries, cities, agencies, hotels] = await Promise.all([
        apiRequest('/marketing-budget/subgroups/'),
        apiRequest('/marketing-budget/markets/'),
        apiRequest('/marketing-budget/types/'),
        apiRequest('/marketing-budget/countries/'),
        apiRequest('/marketing-budget/cities/'),
        apiRequest('/marketing-budget/agencies/'),
        apiRequest('/marketing-budget/hotels/')
      ]);

      setReferenceData({
        maingroups: maingroupsData.results || maingroupsData,
        subgroups: subgroups.results || subgroups,
        markets: markets.results || markets,
        types: types.results || types,
        countries: countries.results || countries,
        cities: cities.results || cities,
        agencies: agencies.results || agencies,
        hotels: hotels.results || hotels
      });

      // Reference data yüklendikten sonra editing data'yı yükle
      if (editingData && editingData.length > 0) {
        loadEditingData(editingData);
      }
    } catch (error) {
      alert(`API Hatası: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Yeni şehir ekleme fonksiyonu
  const handleAddNewCity = async () => {
    if (!newCityName.trim()) {
      alert('Lütfen şehir adı girin!');
      return;
    }

    try {
      setLoading(true);
      const response = await apiRequest('/marketing-budget/cities/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          city_name: newCityName.trim()
        })
      });

      // Şehir listesini güncelle
      setReferenceData(prev => ({
        ...prev,
        cities: [...prev.cities, response]
      }));

      // Modal'ı kapat ve formu temizle
      setIsAddingNewCity(false);
      setNewCityName('');

      alert('Şehir başarıyla eklendi!');
    } catch (error) {
      alert(`Şehir eklenirken hata oluştu: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Şehir arama fonksiyonu
  const getFilteredCities = (entryId) => {
    const searchTerm = citySearchTerm[entryId] || '';
    if (!searchTerm) return referenceData.cities;

    return referenceData.cities.filter(city =>
      city.city_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleMainGroupChange = (e) => {
    const maingroupId = e.target.value;
    setSelectedMainGroup(maingroupId);

    // Ana grup değiştiğinde mevcut kayıtları temizle
    setBudgetEntries([]);
  };

  const handleBudgetTypeChange = (e) => {
    const budgetTypeId = e.target.value;
    setSelectedBudgetType(budgetTypeId);

    // Budget type değiştiğinde mevcut kayıtlardaki budgettype bilgilerini güncelle
    if (budgetEntries.length > 0) {
      const selectedType = referenceData.types.find(type => type.id === parseInt(budgetTypeId));
      if (selectedType) {
        const updatedEntries = budgetEntries.map(entry => ({
          ...entry,
          budgettype_id: selectedType.id,
          budgettype_name: selectedType.budget_type,
          budgettype: selectedType.id
        }));
        setBudgetEntries(updatedEntries);
      }
    }
  };

  const handleYearChange = (e) => {
    const year = e.target.value ? parseInt(e.target.value) : '';
    setBudgetYear(year);

    // Yıl değiştiğinde mevcut kayıtları güncelle
    if (budgetEntries.length > 0) {
      const updatedEntries = budgetEntries.map(entry => ({
        ...entry,
        budget_year: year || null
      }));
      setBudgetEntries(updatedEntries);
    }
  };

  const handleMonthChange = (e) => {
    const month = e.target.value ? parseInt(e.target.value) : '';
    setBudgetMonth(month);

    // Ay değiştiğinde mevcut kayıtları güncelle
    if (budgetEntries.length > 0) {
      const updatedEntries = budgetEntries.map(entry => ({
        ...entry,
        budget_month: month || null
      }));
      setBudgetEntries(updatedEntries);
    }
  };

  const handleAmountChange = (entryId, amount) => {
    setBudgetEntries(prev =>
      prev.map(entry =>
        entry.id === entryId
          ? { ...entry, budget_amount: amount }
          : entry
      )
    );
  };

  const handleFieldChange = (entryId, field, value) => {
    setBudgetEntries(prev =>
      prev.map(entry =>
        entry.id === entryId
          ? { ...entry, [field]: value }
          : entry
      )
    );
  };

  const handleMultipleFieldChange = (entryId, updates) => {
    setBudgetEntries(prev =>
      prev.map(entry =>
        entry.id === entryId
          ? { ...entry, ...updates }
          : entry
      )
    );
  };

  const addNewEntry = () => {
    if (!selectedMainGroup || !selectedBudgetType) {
      alert('Lütfen önce ana grup ve bütçe tipi seçin!');
      return;
    }

    const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));
    const selectedType = referenceData.types.find(type => type.id === parseInt(selectedBudgetType));

    if (!selectedGroup || !selectedType) {
      alert('Ana grup veya bütçe tipi bulunamadı!');
      return;
    }

    // Ana grup bazında zorunlu alan kontrolü
    if (selectedGroup.maingroup_name === 'Web Yazılım') {
      // Web Yazılım için hiçbir alan zorunlu değil
    } else if (selectedGroup.maingroup_name === 'Yurtdışı Temsilci') {
      // Yurtdışı Temsilci için özel kontroller
    } else {
      // Diğer ana gruplar için normal kontroller
    }

    // Yeni boş satır ekle
    const newEntry = {
      id: `temp-${Date.now()}-${Math.random()}`, // Geçici ID
      maingroup_id: selectedGroup.id,
      maingroup_name: selectedGroup.maingroup_name,
      subgroup_id: '',
      subgroup_name: '',
      market_id: '',
      market_name: '',
      budgettype_id: selectedType.id,
      budgettype_name: selectedType.budget_type,
      budgettype: selectedType.id,
      budget_amount: 0,
      budget_year: budgetYear ? parseInt(budgetYear) : null,
      budget_month: budgetMonth ? parseInt(budgetMonth) : null,
      budget_name: `${selectedGroup.maingroup_name} - ${selectedType.budget_type} (${budgetYear})`,
      country: '',
      city: '',
      agency: '',
      departure: '',
      arrival: '',
      person_name: '',
      summary: '',
      start_date: '',
      end_date: '',
      fair_name: '',
      quantity: 0,
      // Sales Call için yeni alanlar
      day_count: 0,
      pax: 0,
      pp: 0,
      // Dijital Pazarlama için yeni alanlar
      region: '',
      eur_curr: null
    };

    setBudgetEntries(prev => [...prev, newEntry]);
  };

  const addBulkEntries = () => {
    if (!selectedMainGroup || !selectedBudgetType) {
      alert('Lütfen önce ana grup ve bütçe tipi seçin!');
      return;
    }

    const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));
    const selectedType = referenceData.types.find(type => type.id === parseInt(selectedBudgetType));

    if (!selectedGroup || !selectedType) {
      alert('Ana grup veya bütçe tipi bulunamadı!');
      return;
    }

    // Bu ana gruba ait alt grupları al
    const relatedSubgroups = referenceData.subgroups.filter(subgroup =>
      subgroup.maingroup === parseInt(selectedMainGroup)
    );

    if (relatedSubgroups.length === 0) {
      alert('Bu ana gruba ait alt grup bulunamadı!');
      return;
    }

    // Her alt grup için her pazara göre kayıt oluştur
    const newEntries = [];
    relatedSubgroups.forEach(subgroup => {
      referenceData.markets.forEach(market => {
        newEntries.push({
          id: `temp-${Date.now()}-${Math.random()}`, // Geçici ID
          maingroup_id: selectedGroup.id,
          maingroup_name: selectedGroup.maingroup_name,
          subgroup_id: subgroup.id,
          subgroup_name: subgroup.subgroup_name,
          market_id: market.id,
          market_name: market.budget_market_name,
          budgettype_id: selectedType.id,
          budgettype_name: selectedType.budget_type,
          budgettype: selectedType.id,
          budget_amount: 0,
          budget_year: budgetYear ? parseInt(budgetYear) : null,
          budget_month: budgetMonth ? parseInt(budgetMonth) : null,
          country: '',
          city: '',
          agency: '',
          departure: '',
          arrival: '',
          person_name: '',
          summary: '',
          start_date: '',
          end_date: '',
          fair_name: '',
          quantity: 0,
          // Sales Call için yeni alanlar
          day_count: 0,
          pax: 0,
          pp: 0
        });
      });
    });

    setBudgetEntries(prev => [...prev, ...newEntries]);

    alert(`${newEntries.length} satır eklendi!`);
  };

  const removeEntry = (entryId) => {
    setBudgetEntries(prev => prev.filter(entry => entry.id !== entryId));
  };

  // Kalıcı silme: DB'de olan kayıtları DELETE ile sil, yoksa sadece state'ten kaldır
  // Sıralama fonksiyonu
  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: field, direction });
  };

  // Sıralanmış entries
  const getSortedEntries = () => {
    if (!sortConfig.key) return budgetEntries;

    return [...budgetEntries].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Özel alan kontrolü
      if (sortConfig.key === 'subgroup_name') {
        aValue = a.subgroup_name || '';
        bValue = b.subgroup_name || '';
      } else if (sortConfig.key === 'market_name') {
        aValue = a.market_name || '';
        bValue = b.market_name || '';
      } else if (sortConfig.key === 'budget_amount') {
        aValue = parseFloat(a.budget_amount) || 0;
        bValue = parseFloat(b.budget_amount) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleDelete = async (entryId) => {
    const entry = budgetEntries.find(e => e.id === entryId);
    if (!entry) return;

    const confirmed = window.confirm('Bu kaydı silmek istediğinize emin misiniz?');
    if (!confirmed) return;

    try {
      // ID kontrolü - string veya number olabilir, geçici ID'ler "temp-" ile başlar
      const isTemporaryId = typeof entry.id === 'string' && entry.id.startsWith('temp-');
      const isNumericId = entry.id && !isNaN(entry.id) && (Number.isInteger(Number(entry.id)) || !isTemporaryId);

      if (isNumericId) {
        // DB kaydıdır - backend'e silme isteği gönder
        const response = await apiRequest(`/marketing-budget/registries/${entry.id}/`, {
          method: 'DELETE'
        });

        // Response kontrolü - Backend artık { success: true } JSON response döndürüyor
        if (response && response.success === true) {
          // Backend'de başarıyla silindi, frontend'den de kaldır
          removeEntry(entryId);
        } else {
          // Beklenmeyen response veya hata
          const errorMsg = response?.message || 'Silme işlemi başarısız oldu. Lütfen tekrar deneyin.';
          throw new Error(errorMsg);
        }
      } else {
        // Yeni eklenen, henüz kaydedilmemiş satır - sadece frontend'den kaldır
        removeEntry(entryId);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Kayıt silinirken bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
    }
  };

  // Satır çoğaltma fonksiyonu
  const duplicateEntry = (entryId) => {
    const entryToDuplicate = budgetEntries.find(entry => entry.id === entryId);
    if (!entryToDuplicate) return;

    // Modal'dan seçili budget type'ı al
    const selectedType = referenceData.types.find(type => type.id === parseInt(selectedBudgetType));

    // Çoğaltılan satır için yeni ID oluştur ve bazı alanları temizle
    const duplicatedEntry = {
      ...entryToDuplicate,
      id: `temp-${Date.now()}-${Math.random()}`, // Yeni benzersiz geçici ID
      budget_amount: '', // Tutar alanını temizle
      // Modal'dan seçili budget type bilgilerini kullan
      budgettype_id: selectedType?.id || entryToDuplicate.budgettype_id,
      budgettype_name: selectedType?.budget_type || entryToDuplicate.budgettype_name,
      budgettype: selectedType?.id || entryToDuplicate.budgettype,
      // Diğer hassas alanları da temizleyebiliriz
      summary: '',
      person_name: '',
      agency: selectedMainGroup === 'Yılbaşı Hediye' ? entryToDuplicate.agency : '', // Yılbaşı Hediye'de acente korunur
    };

    setBudgetEntries(prev => [...prev, duplicatedEntry]);
  };

  const handleSave = async () => {
    try {
      // Web Yazılım için özel filtreleme
      const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));

      let entriesToSave;
      if (selectedGroup?.maingroup_name === 'Web Yazılım') {
        // Web Yazılım için sadece tutar kontrolü
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için tutar girmelisiniz.');
          return;
        }
      } else if (selectedGroup?.maingroup_name === 'Yurtdışı Temsilci') {
        // Yurtdışı Temsilci için özel kontrol: Ülke, Market, Subgroup, Tutar
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0 &&
          entry.country && entry.market_id && entry.subgroup_id
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için ülke, pazar, alt grup ve bütçe miktarı girmelisiniz.');
          return;
        }
      } else if (selectedGroup?.maingroup_name === 'Temsil Ağırlama') {
        // Temsil Ağırlama için özel kontrol: Otel, Alt Grup, Tutar (PAX 0 olabilir, Market zorunlu değil)
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0 &&
          entry.hotel_name && entry.subgroup_id &&
          (entry.pax !== undefined && entry.pax !== null) // PAX tanımlı olmalı ama 0 olabilir
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için otel, alt grup ve bütçe miktarı girmelisiniz. PAX 0 olabilir.');
          return;
        }
      } else if (selectedGroup?.maingroup_name === 'AKKA Bonus') {
        // AKKA Bonus için alt grup ve tutar zorunlu
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0 &&
          entry.subgroup_id
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için alt grup ve bütçe miktarı girmelisiniz.');
          return;
        }
      } else if (selectedGroup?.maingroup_name === 'Dijital Pazarlama') {
        // Dijital Pazarlama için özel kontrol: Ay, Bölge, Alt Grup, Tutar, Euro Kur
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0 &&
          entry.budget_month && entry.region && entry.subgroup_id && entry.eur_curr
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için ay, bölge, alt grup, tutar ve euro kur girmelisiniz.');
          return;
        }
      } else if (selectedGroup?.maingroup_name === 'Promosyon') {
        // Promosyon için özel kontrol: Pazar, Alt Grup, Tutar (Adet zorunlu değil, 0 olabilir)
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0 &&
          entry.subgroup_id && entry.market_id
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için alt grup, pazar ve bütçe miktarı girmelisiniz.');
          return;
        }
      } else {
        // Diğer ana gruplar için normal filtreleme
        entriesToSave = budgetEntries.filter(entry =>
          entry.budget_amount && parseFloat(entry.budget_amount) > 0 &&
          entry.subgroup_id && entry.market_id
        );

        if (entriesToSave.length === 0) {
          alert('En az bir kayıt için alt grup, pazar ve bütçe miktarı girmelisiniz.');
          return;
        }
      }

      // Bulk save için verileri hazırla
      const toCreate = [];
      const toUpdate = [];

      entriesToSave.forEach(entry => {
        // Market kontrolü - Promosyon için market zorunlu
        const marketValue = (['Temsil Ağırlama', 'AKKA Bonus'].includes(selectedGroup?.maingroup_name) || selectedGroup?.maingroup_name === 'Web Yazılım' || selectedGroup?.maingroup_name === 'Dijital Pazarlama')
          ? null
          : (entry.market_id || entry.market || null);

        const payload = {
          maingroup: entry.maingroup || entry.maingroup_id,
          subgroup: entry.subgroup || entry.subgroup_id,
          market: marketValue,
          budgettype_name: entry.budgettype_name,
          budget_amount: entry.budget_amount,
          budget_year: entry.budget_year || null,
          budget_month: entry.budget_month || null,
          budget_name: entry.budget_name || '',
          budgettype: entry.budgettype || entry.budgettype_id,
          country: selectedGroup?.maingroup_name === 'AKKA Bonus' ? '' : (entry.country || ''),
          city: selectedGroup?.maingroup_name === 'AKKA Bonus' ? '' : (entry.city || ''),
          agency: selectedGroup?.maingroup_name === 'AKKA Bonus' ? '' : (entry.agency || ''),
          departure: entry.departure || null,
          arrival: entry.arrival || null,
          person_name: entry.person_name || '',
          summary: selectedGroup?.maingroup_name === 'AKKA Bonus' ? '' : (entry.summary || ''),
          start_date: entry.start_date || null,
          end_date: entry.end_date || null,
          fair_name: entry.fair_name || '',
          quantity: entry.quantity || 0,
          day_count: entry.day_count || 0,
          pax: entry.pax || 0,
          pp: entry.pp || 0,
          hotel_name: selectedGroup?.maingroup_name === 'Temsil Ağırlama' ? (entry.hotel_name || '') : '',
          region: entry.region || '',
          eur_curr: entry.eur_curr || null,
          meal_cost: entry.meal_cost || 0,
          cargo: entry.cargo || 0,
          transport: entry.transport || 0,
          others: entry.others || 0,
          allowance: entry.allowance || 0,
          extra: entry.extra || 0
        };

        // ID kontrolü - string veya number olabilir, geçici ID'ler "temp-" ile başlar
        const isTemporaryId = typeof entry.id === 'string' && entry.id.startsWith('temp-');
        const isNumericId = entry.id && !isNaN(entry.id) && (Number.isInteger(Number(entry.id)) || !isTemporaryId);

        if (isNumericId) {
          // Güncelleme - ID'yi number'a çevir
          toUpdate.push({
            id: parseInt(entry.id),
            ...payload
          });
        } else {
          // Yeni kayıt
          toCreate.push(payload);
        }
      });

      // Tek bir istek gönder
      if (toCreate.length > 0 || toUpdate.length > 0) {
        console.log('Saving entries:', { toCreate: toCreate.length, toUpdate: toUpdate.length });
        console.log('Sample entry to create:', toCreate.length > 0 ? toCreate[0] : 'N/A');
        console.log('Sample entry to update:', toUpdate.length > 0 ? toUpdate[0] : 'N/A');

        try {
          const response = await apiRequest('/marketing-budget/registries/bulk_save/', {
            method: 'POST',
            body: JSON.stringify({
              to_create: toCreate,
              to_update: toUpdate
            })
          });

          console.log('Backend response:', response);

          // Backend response'unu kontrol et
          if (!response || !response.success) {
            let errorMessage = 'Kayıt sırasında hata oluştu.';

            if (response && response.message) {
              errorMessage = response.message;
            }

            if (response && response.errors && response.errors.length > 0) {
              const errorDetails = response.errors.map(err => {
                if (typeof err.errors === 'object') {
                  return Object.entries(err.errors).map(([field, messages]) =>
                    `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
                  ).join('\n');
                }
                return JSON.stringify(err.errors);
              }).join('\n\n');

              errorMessage += '\n\nDetaylar:\n' + errorDetails;
            }

            console.error('Save error:', errorMessage);
            alert(errorMessage);
            return; // Hata durumunda işlemi durdur
          }

          // Başarılı kayıt sayısını göster
          const created = response.created || 0;
          const updated = response.updated || 0;
          const totalSaved = created + updated;

          if (totalSaved > 0) {
            // Önce parent component'e kayıt başarılı olduğunu bildir (verileri yenilemesi için)
            onSave();
            // Sonra modal'ı kapat
            handleClose();
            // Kısa bir gecikme ile alert göster (verilerin yüklenmesi için zaman tanı)
            setTimeout(() => {
              alert(`${totalSaved} kayıt başarıyla ${created > 0 && updated > 0 ? 'eklendi/güncellendi' : created > 0 ? 'eklendi' : 'güncellendi'}.`);
            }, 100);
          } else {
            alert('Hiçbir kayıt kaydedilemedi. Lütfen verileri kontrol edin.');
          }
        } catch (error) {
          // Network veya diğer hatalar
          console.error('Save error (catch):', error);
          alert(`Kayıt sırasında hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
        }
      } else {
        alert('Kaydedilecek kayıt bulunamadı.');
      }
    } catch (error) {
      // Dış try-catch - beklenmeyen hatalar için
      console.error('Unexpected save error:', error);
      alert(`Kayıt sırasında beklenmeyen bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
    }
  };

  const handleClose = () => {
    setSelectedMainGroup('');
    setBudgetEntries([]);
    setBudgetYear(new Date().getFullYear());
    setBudgetMonth(new Date().getMonth() + 1);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] outline-none focus:outline-none">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-[calc(100vw-2cm)] h-[calc(100vh-2cm)] overflow-y-auto transform transition-all animate-in fade-in zoom-in duration-300">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingData ? 'Bütçe Kaydını Düzenle' : 'Yeni Bütçe Kaydı'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Ana Grup Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ana Grup Seçin
                {editingData && editingData.length > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    (Düzenleme modunda değiştirilemez)
                  </span>
                )}
              </label>
              {loading ? (
                <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Ana gruplar yükleniyor...
                  </div>
                </div>
              ) : (
                <select
                  value={selectedMainGroup}
                  onChange={handleMainGroupChange}
                  disabled={editingData && editingData.length > 0}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${editingData && editingData.length > 0
                    ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed opacity-60'
                    : ''
                    }`}
                >
                  <option value="">Ana Grup Seçin</option>
                  {referenceData.maingroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.maingroup_name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Budget Type Seçimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bütçe Tipi Seçin
                {editingData && editingData.length > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    (Düzenleme modunda değiştirilemez)
                  </span>
                )}
              </label>
              {loading ? (
                <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Bütçe tipleri yükleniyor...
                  </div>
                </div>
              ) : (
                <select
                  value={selectedBudgetType}
                  onChange={handleBudgetTypeChange}
                  disabled={editingData && editingData.length > 0}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${editingData && editingData.length > 0
                    ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed opacity-60'
                    : ''
                    }`}
                >
                  <option value="">Bütçe Tipi Seçin</option>
                  {referenceData.types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.budget_type}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Yıl ve Ay */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Yıl
                </label>
                <select
                  value={budgetYear}
                  onChange={handleYearChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Yıl Seçin</option>
                  {Array.from({ length: 10 }, (_, i) => 2020 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ay
                </label>
                <select
                  value={budgetMonth}
                  onChange={handleMonthChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Ay Seçin</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bütçe Adı Gösterimi */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bütçe Adı
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                {(() => {
                  const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));
                  const selectedType = referenceData.types.find(type => type.id === parseInt(selectedBudgetType));

                  if (!selectedGroup || !selectedType) {
                    return 'Ana grup ve bütçe tipi seçin';
                  }

                  const groupName = selectedGroup.maingroup_name;
                  const typeName = selectedType.budget_type;

                  if (budgetYear && budgetMonth) {
                    const monthName = new Date(2024, budgetMonth - 1).toLocaleString('tr-TR', { month: 'long' });
                    return `${groupName} - ${typeName} (${budgetYear} ${monthName})`;
                  } else if (budgetYear) {
                    return `${groupName} - ${typeName} (${budgetYear})`;
                  } else {
                    return `${groupName} - ${typeName}`;
                  }
                })()}
              </div>
            </div>



            {/* Bütçe Kayıtları Tablosu */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Bütçe Kayıtları
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      {(() => {
                        const selGroupId = parseInt(selectedMainGroup);
                        const selectedGroup = referenceData.maingroups.find(group => parseInt(group.id) === selGroupId);
                        const fieldConfig = getFieldConfig(selectedGroup?.maingroup_name || '');
                        // Ana gruba göre özel etiketler
                        const getFieldLabel = (field) => {
                          const selGroupId = parseInt(selectedMainGroup);
                          const selectedGroup = (referenceData.maingroups || []).find(group => parseInt(group.id) === selGroupId);
                          const maingroupName = selectedGroup?.maingroup_name || '';

                          const baseLabels = {
                            subgroup: 'Alt Grup',
                            market: 'Pazar',
                            country: 'Ülke',
                            city: 'Şehir',
                            agency: 'Acenta',
                            departure: 'Gidiş',
                            arrival: 'Dönüş',
                            person_name: 'Kişi Adı',
                            start_date: 'Başlangıç Tarihi',
                            end_date: 'Bitiş Tarihi',
                            fair_name: 'Fuar Adı',
                            quantity: 'Adet',
                            budget_amount: 'Tutar',
                            budget_month: 'Ay',
                            // Sales Call için yeni alanlar
                            day_count: 'Gün',
                            pax: 'Kişi Sayısı',
                            pp: 'PP',
                            meal_cost: 'Yemek Bedeli',
                            // Dijital Pazarlama için yeni alanlar
                            region: 'Bölge',
                            eur_curr: 'Euro Kur',
                            cargo: 'Kargo',
                            hotel: 'Otel',
                            transport: 'Ulaşım',
                            others: 'Diğerleri',
                            allowance: 'Harcirah',
                            extra: 'Extra'
                          };

                          // Özel etiketler
                          if (field === 'summary') {
                            switch (maingroupName) {
                              case 'Workshop':
                              case 'Work Shop Road Show':
                                return 'Notlar';
                              default:
                                return 'Özet';
                            }
                          }

                          return baseLabels[field] || field;
                        };

                        return fieldConfig.showFields.map(field => (
                          <th
                            key={field}
                            onClick={() => handleSort(field === 'subgroup' ? 'subgroup_name' : field === 'market' ? 'market_name' : field)}
                            className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-medium text-gray-900 dark:text-white cursor-pointer select-none"
                          >
                            <div className="flex items-center gap-1">
                              <span>{getFieldLabel(field)}</span>
                              {sortConfig.key === (field === 'subgroup' ? 'subgroup_name' : field === 'market' ? 'market_name' : field) && (
                                sortConfig.direction === 'asc' ?
                                  <ChevronUpIcon className="h-4 w-4" /> :
                                  <ChevronDownIcon className="h-4 w-4" />
                              )}
                            </div>
                          </th>
                        ));
                      })()}
                      <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-medium text-gray-900 dark:text-white">
                        İşlem
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetEntries.length === 0 ? (
                      <tr>
                        <td colSpan={(() => {
                          const selGroupId = parseInt(selectedMainGroup);
                          const selectedGroup = (referenceData.maingroups || []).find(group => parseInt(group.id) === selGroupId);
                          const fieldConfig = getFieldConfig(selectedGroup?.maingroup_name || '');
                          return fieldConfig.showFields.length + 1; // +1 for İşlem column
                        })()} className="border border-gray-300 dark:border-gray-600 px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          Henüz kayıt eklenmedi. "Yeni Satır Ekle" butonuna tıklayarak kayıt ekleyebilirsiniz.
                        </td>
                      </tr>
                    ) : (
                      getSortedEntries().map((entry) => {
                        const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));
                        const fieldConfig = getFieldConfig(selectedGroup?.maingroup_name || '');

                        return (
                          <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            {fieldConfig.showFields.map(field => {
                              switch (field) {
                                case 'subgroup':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <select
                                        value={entry.subgroup_id || ''}
                                        onChange={(e) => {
                                          const subgroupId = e.target.value;
                                          const subId = parseInt(subgroupId);
                                          const subgroup = referenceData.subgroups.find(sub => parseInt(sub.id) === subId);
                                          handleMultipleFieldChange(entry.id, {
                                            subgroup_id: subgroupId,
                                            subgroup_name: subgroup ? subgroup.subgroup_name : ''
                                          });
                                        }}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      >
                                        <option value="">Alt Grup Seçin</option>
                                        {referenceData.subgroups
                                          .filter(subgroup => parseInt(subgroup.maingroup) === parseInt(selectedMainGroup))
                                          .map((subgroup) => (
                                            <option key={subgroup.id} value={subgroup.id}>
                                              {subgroup.subgroup_name}
                                            </option>
                                          ))}
                                      </select>
                                    </td>
                                  );
                                case 'market':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <select
                                        value={entry.market_id || ''}
                                        onChange={(e) => {
                                          const marketId = e.target.value;
                                          const mktId = parseInt(marketId);
                                          const market = referenceData.markets.find(m => parseInt(m.id) === mktId);
                                          handleMultipleFieldChange(entry.id, {
                                            market_id: marketId,
                                            market_name: market ? market.budget_market_name : ''
                                          });
                                        }}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      >
                                        <option value="">Pazar Seçin</option>
                                        {referenceData.markets.map((market) => (
                                          <option key={market.id} value={market.id}>
                                            {market.budget_market_name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                case 'country':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <select
                                        value={entry.country || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'country', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      >
                                        <option value="">Ülke Seçin</option>
                                        {referenceData.countries.map((country) => (
                                          <option key={country.id} value={country.country_name}>
                                            {country.country_name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                case 'city':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <div className="relative">
                                        <input
                                          id={`city-input-${entry.id}`}
                                          type="text"
                                          value={entry.city || ''}
                                          onChange={(e) => {
                                            handleFieldChange(entry.id, 'city', e.target.value);
                                            setCitySearchTerm(prev => ({ ...prev, [entry.id]: e.target.value }));
                                            setShowCityDropdown(prev => ({ ...prev, [entry.id]: true }));
                                          }}
                                          onFocus={() => setShowCityDropdown(prev => ({ ...prev, [entry.id]: true }))}
                                          placeholder="Şehir ara veya seç..."
                                          className="w-full px-2 py-1 pr-8 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => setIsAddingNewCity(true)}
                                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                          title="Yeni Şehir Ekle"
                                        >
                                          <PlusIcon className="h-4 w-4" />
                                        </button>
                                        {showCityDropdown[entry.id] && (
                                          <div id={`city-dropdown-${entry.id}`} className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            {getFilteredCities(entry.id).length > 0 ? (
                                              getFilteredCities(entry.id).map((city) => (
                                                <div
                                                  key={city.id}
                                                  onClick={() => {
                                                    handleFieldChange(entry.id, 'city', city.city_name);
                                                    setCitySearchTerm(prev => ({ ...prev, [entry.id]: '' }));
                                                    setShowCityDropdown(prev => ({ ...prev, [entry.id]: false }));
                                                  }}
                                                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-gray-900 dark:text-white text-sm"
                                                >
                                                  {city.city_name}
                                                </div>
                                              ))
                                            ) : (
                                              <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                                Sonuç bulunamadı
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  );
                                case 'agency':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <select
                                        value={entry.agency || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'agency', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      >
                                        <option value="">Acenta Seçin</option>
                                        {referenceData.agencies.map((agency) => (
                                          <option key={agency.id} value={agency.agency_name}>
                                            {agency.agency_name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                case 'hotel':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <select
                                        value={entry.hotel_name || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'hotel_name', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      >
                                        <option value="">Otel Seçin</option>
                                        {referenceData.hotels.map((hotel) => (
                                          <option key={hotel.id} value={hotel.hotel_name}>
                                            {hotel.hotel_name}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                case 'departure':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="date"
                                        value={entry.departure || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'departure', e.target.value)}
                                        placeholder="Gidiş tarihi seçin"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'arrival':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="date"
                                        value={entry.arrival || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'arrival', e.target.value)}
                                        placeholder="Dönüş tarihi seçin"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'person_name':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="text"
                                        value={entry.person_name || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'person_name', e.target.value)}
                                        placeholder="Kişi adı girin"
                                        maxLength="60"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'summary':
                                  const selectedGroup = (referenceData.maingroups || []).find(group => parseInt(group.id) === parseInt(selectedMainGroup));
                                  const maingroupName = selectedGroup?.maingroup_name || '';
                                  const summaryPlaceholder = (maingroupName === 'Workshop' || maingroupName === 'Work Shop Road Show') ? 'Notlar girin' : 'Özet girin';

                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="text"
                                        value={entry.summary || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'summary', e.target.value)}
                                        placeholder={summaryPlaceholder}
                                        maxLength="200"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'start_date':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="date"
                                        value={entry.start_date || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'start_date', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'end_date':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="date"
                                        value={entry.end_date || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'end_date', e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'fair_name':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="text"
                                        value={entry.fair_name || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'fair_name', e.target.value)}
                                        placeholder="Fuar adı girin"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'quantity':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.quantity || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'quantity', parseInt(e.target.value) || 0)}
                                        placeholder="Adet girin"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'budget_amount':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.budget_amount}
                                        onChange={(e) => handleAmountChange(entry.id, e.target.value)}
                                        placeholder="Tutar girin"
                                        step="0.01"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                      />
                                    </td>
                                  );
                                // Sales Call için yeni alanlar
                                case 'day_count':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.day_count || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'day_count', parseInt(e.target.value) || 0)}
                                        placeholder="Gün sayısı"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'pax':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.pax || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'pax', parseInt(e.target.value) || 0)}
                                        placeholder="Kişi sayısı"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'pp':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.pp || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'pp', parseFloat(e.target.value) || 0)}
                                        placeholder="PP tutarı"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'region':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="text"
                                        value={entry.region || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'region', e.target.value)}
                                        placeholder="Bölge"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'eur_curr':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.eur_curr || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'eur_curr', parseFloat(e.target.value) || 0)}
                                        placeholder="Euro Kur"
                                        step="0.0001"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'meal_cost':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.meal_cost || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'meal_cost', parseFloat(e.target.value) || 0)}
                                        placeholder="Yemek bedeli"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'cargo':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.cargo || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'cargo', parseFloat(e.target.value) || 0)}
                                        placeholder="Kargo tutarı"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'hotel':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.hotel || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'hotel', parseFloat(e.target.value) || 0)}
                                        placeholder="Otel tutarı"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'transport':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.transport || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'transport', parseFloat(e.target.value) || 0)}
                                        placeholder="Ulaşım tutarı"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'others':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.others || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'others', parseFloat(e.target.value) || 0)}
                                        placeholder="Diğer tutarlar"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'allowance':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.allowance || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'allowance', parseFloat(e.target.value) || 0)}
                                        placeholder="Harcirah tutarı"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'extra':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <input
                                        type="number"
                                        value={entry.extra || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'extra', parseFloat(e.target.value) || 0)}
                                        placeholder="Extra tutar"
                                        step="0.01"
                                        min="0"
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      />
                                    </td>
                                  );
                                case 'budget_month':
                                  return (
                                    <td key={field} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                      <select
                                        value={entry.budget_month || ''}
                                        onChange={(e) => handleFieldChange(entry.id, 'budget_month', parseInt(e.target.value) || null)}
                                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      >
                                        <option value="">Ay Seçin</option>
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                          <option key={month} value={month}>
                                            {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                default:
                                  return null;
                              }
                            })}
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center">
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() => duplicateEntry(entry.id)}
                                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                  title="Satırı Çoğalt"
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(entry.id)}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                  title="Satırı Sil"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}

                    {/* Sabit Alt Başlık Satırı */}
                    <tr className="bg-gray-50 dark:bg-gray-700 sticky bottom-0 z-20 shadow-lg border-t-2 border-gray-300 dark:border-gray-600">
                      {(() => {
                        const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));
                        const fieldConfig = getFieldConfig(selectedGroup?.maingroup_name || '');
                        // Ana gruba göre özel etiketler
                        const getFieldLabel = (field) => {
                          const selectedGroup = referenceData.maingroups.find(group => group.id === parseInt(selectedMainGroup));
                          const maingroupName = selectedGroup?.maingroup_name || '';

                          const baseLabels = {
                            subgroup: 'Alt Grup',
                            market: 'Pazar',
                            country: 'Ülke',
                            city: 'Şehir',
                            agency: 'Acenta',
                            departure: 'Gidiş',
                            arrival: 'Dönüş',
                            person_name: 'Kişi Adı',
                            start_date: 'Başlangıç Tarihi',
                            end_date: 'Bitiş Tarihi',
                            fair_name: 'Fuar Adı',
                            quantity: 'Adet',
                            budget_amount: 'Tutar',
                            budget_month: 'Ay',
                            // Sales Call için yeni alanlar
                            day_count: 'Gün',
                            pax: 'Kişi Sayısı',
                            pp: 'PP',
                            meal_cost: 'Yemek Bedeli',
                            // Dijital Pazarlama için yeni alanlar
                            region: 'Bölge',
                            eur_curr: 'Euro Kur',
                            cargo: 'Kargo',
                            hotel: 'Otel',
                            transport: 'Ulaşım',
                            others: 'Diğerleri',
                            allowance: 'Harcirah',
                            extra: 'Extra'
                          };

                          // Özel etiketler
                          if (field === 'summary') {
                            switch (maingroupName) {
                              case 'Workshop':
                              case 'Work Shop Road Show':
                                return 'Notlar';
                              default:
                                return 'Özet';
                            }
                          }

                          return baseLabels[field] || field;
                        };

                        return (
                          <>
                            {fieldConfig.showFields.map(field => (
                              <th
                                key={field}
                                className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700"
                              >
                                {getFieldLabel(field)}
                              </th>
                            ))}
                            <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700">
                              İşlem
                            </th>
                          </>
                        );
                      })()}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                <p>• <strong>Yeni Satır Ekle:</strong> Tek tek boş satır ekler, her alanı manuel seçersiniz</p>
                <p>• <strong>Toplu Satır Ekle:</strong> Tüm alt grup-pazar kombinasyonlarını otomatik oluşturur</p>
                <p>• Her satırda ülke, şehir, acenta seçimi yapın ve bütçe miktarı girin</p>
                <p>• Sadece tam dolu kayıtlar kaydedilir</p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between items-center mt-6">
            <button
              type="button"
              onClick={addNewEntry}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Satır Ekle
            </button>
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={budgetEntries.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Kaydet ({budgetEntries.filter(entry => entry.budget_amount && parseFloat(entry.budget_amount) > 0).length} kayıt)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Yeni Şehir Ekleme Modal'ı */}
      {isAddingNewCity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Yeni Şehir Ekle
              </h3>
              <button
                onClick={() => {
                  setIsAddingNewCity(false);
                  setNewCityName('');
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Şehir Adı
              </label>
              <input
                type="text"
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNewCity();
                  }
                }}
                placeholder="Şehir adını girin..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                autoFocus
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setIsAddingNewCity(false);
                  setNewCityName('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleAddNewCity}
                disabled={!newCityName.trim() || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Ekleniyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default MarketingBudgetModal;